import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../../contexts/LanguageContext";
import {
  getProviderServices,
  getFreelancerServiceTitle,
  getFreelancerServiceSummary,
  formatServiceCurrency,
} from "../../../api/freelancerServices";
import StarRating from "../../../components/ui/StarRating";
import logger from "../../../utils/logger";
import {
  Wrench,
  PlusCircle,
  ExternalLink,
  Edit3,
  Clock,
  CheckCircle2,
  AlertCircle,
  ShoppingBag,
  Loader2,
  RefreshCw,
} from "lucide-react";
import "./MyServicesTab.css";

const fallbackImage =
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80";

export default function MyServicesTab() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const loadServices = async (isManual = false) => {
    try {
      if (isManual) setRefreshing(true);
      else setLoading(true);
      setError("");

      const data = await getProviderServices();
      setServices(data || []);
    } catch (err) {
      logger.error("Failed to load provider services", err);
      setError(
        isAr
          ? "تعذر تحميل الخدمات المنشورة."
          : "Failed to load your published services."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadServices();
  }, [isAr]);

  if (loading) {
    return (
      <div className="myservices-loading">
        <Loader2 className="myservices-spinner" size={36} />
        <p>{isAr ? "جارِ تحميل الخدمات المنشورة..." : "Loading published services..."}</p>
      </div>
    );
  }

  const activeCount = services.filter(
    (s) =>
      s.is_active !== false &&
      String(s.approval_status || s.status).toLowerCase() === "approved"
  ).length;

  const pendingCount = services.filter(
    (s) => String(s.approval_status || s.status).toLowerCase() === "pending"
  ).length;

  return (
    <div className="myservices-container" dir={isAr ? "rtl" : "ltr"}>
      {/* Header Banner */}
      <div className="myservices-header">
        <div className="myservices-header-info">
          <div className="myservices-title-row">
            <h2 className="myservices-title">
              {isAr ? "الخدمات المنشورة" : "My Published Services"}
            </h2>
            <span className="myservices-badge-count">{services.length}</span>
          </div>
          <p className="myservices-subtitle">
            {isAr
              ? "إدارة متابعة وتعديل الخدمات التي تقدمها عبر المنصة"
              : "Manage, track and update services published on the marketplace"}
          </p>
        </div>

        <div className="myservices-header-actions">
          <button
            type="button"
            className="myservices-btn-refresh"
            onClick={() => loadServices(true)}
            disabled={refreshing}
            title={isAr ? "تحديث القائمة" : "Refresh list"}
          >
            <RefreshCw size={16} className={refreshing ? "spin" : ""} />
          </button>

          <Link to="/provider-dashboard" className="myservices-btn-add">
            <PlusCircle size={17} />
            <span>{isAr ? "إضافة خدمة جديدة" : "Add New Service"}</span>
          </Link>
        </div>
      </div>

      {/* Summary KPI Pills */}
      <div className="myservices-kpi-grid">
        <div className="myservices-kpi-card">
          <div className="myservices-kpi-icon kpi-total">
            <Wrench size={18} />
          </div>
          <div className="myservices-kpi-text">
            <span className="myservices-kpi-num">{services.length}</span>
            <span className="myservices-kpi-lbl">{isAr ? "إجمالي الخدمات" : "Total Services"}</span>
          </div>
        </div>

        <div className="myservices-kpi-card">
          <div className="myservices-kpi-icon kpi-active">
            <CheckCircle2 size={18} />
          </div>
          <div className="myservices-kpi-text">
            <span className="myservices-kpi-num">{activeCount}</span>
            <span className="myservices-kpi-lbl">{isAr ? "خدمات معتمدة ونشطة" : "Active Approved"}</span>
          </div>
        </div>

        <div className="myservices-kpi-card">
          <div className="myservices-kpi-icon kpi-pending">
            <Clock size={18} />
          </div>
          <div className="myservices-kpi-text">
            <span className="myservices-kpi-num">{pendingCount}</span>
            <span className="myservices-kpi-lbl">{isAr ? "قيد المراجعة" : "Pending Review"}</span>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="myservices-error-box">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Services List / Grid */}
      {services.length === 0 ? (
        <div className="myservices-empty-state">
          <div className="myservices-empty-icon">
            <Wrench size={42} strokeWidth={1.5} />
          </div>
          <h3>{isAr ? "لم تقم بنشر أي خدمات بعد" : "No Published Services Yet"}</h3>
          <p>
            {isAr
              ? "انشر خدمتك الأولى وابدأ في استقبال طلبات العملاء وزيادة أرباحك على المنصة."
              : "Publish your first service to start receiving client orders and earning on the platform."}
          </p>
          <Link to="/provider-dashboard" className="myservices-btn-add">
            <PlusCircle size={18} />
            <span>{isAr ? "إضافة خدمتك الأولى" : "Publish First Service"}</span>
          </Link>
        </div>
      ) : (
        <div className="myservices-grid">
          {services.map((service) => {
            const title = getFreelancerServiceTitle(service, isAr);
            const summary = getFreelancerServiceSummary(service, isAr);
            const ref = service.slug || service.id;
            const priceFormatted = formatServiceCurrency(
              service.price || 0,
              service.currency || "USD"
            );
            const status = String(
              service.approval_status || service.status || "pending"
            ).toLowerCase();

            let statusLabel = isAr ? "معتمد ونشط" : "Approved & Active";
            let statusClass = "status-approved";

            if (status === "pending") {
              statusLabel = isAr ? "قيد المراجعة" : "Pending Review";
              statusClass = "status-pending";
            } else if (status === "rejected") {
              statusLabel = isAr ? "مرفوض" : "Rejected";
              statusClass = "status-rejected";
            } else if (service.is_active === false) {
              statusLabel = isAr ? "غير نشط" : "Inactive";
              statusClass = "status-inactive";
            }

            return (
              <div key={service.id} className="myservice-card">
                <div className="myservice-cover-wrapper">
                  <img
                    src={service.cover_image || fallbackImage}
                    alt={title}
                    className="myservice-cover-img"
                    onError={(e) => {
                      e.currentTarget.src = fallbackImage;
                    }}
                  />
                  <span className={`myservice-status-badge ${statusClass}`}>
                    {statusLabel}
                  </span>
                </div>

                <div className="myservice-card-content">
                  <div className="myservice-category-tag">
                    {service.category || (isAr ? "خدمة برمجية" : "Service")}
                  </div>

                  <h3 className="myservice-card-title">{title}</h3>
                  {summary && <p className="myservice-card-summary">{summary}</p>}

                  <div className="myservice-meta-row">
                    <div className="myservice-rating">
                      <StarRating
                        value={Number(service.average_rating || service.rating || 0)}
                        size={14}
                        showValue
                      />
                    </div>
                    <div className="myservice-orders-count">
                      <ShoppingBag size={14} />
                      <span>
                        {service.orders_count || 0} {isAr ? "طلب" : "orders"}
                      </span>
                    </div>
                  </div>

                  <div className="myservice-card-footer">
                    <div className="myservice-price-tag">{priceFormatted}</div>

                    <div className="myservice-actions">
                      <Link
                        to={`/freelancers/${ref}`}
                        className="myservice-action-btn btn-view"
                        title={isAr ? "معاينة في السوق" : "View in Marketplace"}
                      >
                        <ExternalLink size={15} />
                        <span>{isAr ? "عرض" : "View"}</span>
                      </Link>

                      <Link
                        to="/provider-dashboard"
                        className="myservice-action-btn btn-edit"
                        title={isAr ? "إدارة وتعديل" : "Edit service"}
                      >
                        <Edit3 size={15} />
                        <span>{isAr ? "إدارة" : "Edit"}</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
